import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from './auth.service';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { ChargesCycle, IChargesCycle } from '../models/charges-cycle.model';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';

@Injectable({
  providedIn: 'root'
})
export class ChargesCycleService {

  public loading: boolean = false;
  public chargesCycles: ChargesCycle[] = [];

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    private spinnerService: SpinnerService,
    private toastService: ToastService,
  ) {}

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async create(
    neighborhoodId: string,
    body: { cutoffDay: number; maxPaymentDay: number }
  ): Promise<boolean> {
    let success = false;
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/charges-cycles`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Creando ciclo de cobro...';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>((resolve, reject) => {
        this.http.post<IServerResponse>(
          urlApi,
          JSON.stringify(body),
          {
            headers: {
              Authorization: token.toString(),
              'Content-Type': 'application/json',
            },
          }
        ).subscribe({
          next: (response) => resolve(new ServerResponse(response)),
          error: (error) => {
            if (error.error) {
              resolve(new ServerResponse(error.error));
            } else {
              reject(error);
            }
          },
        });
      });

      if (response.statusCode === 201) {
        success = true;
        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white',
        });
        await this.getAll(neighborhoodId);
      } else {
        this.toastService.show({
          header: 'Error',
          body: response.message,
          classname: 'bg-danger text-white',
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.spinnerService.loading = false;
      this.spinnerService.message = '';
    }

    return success;
  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  async getAll(
    neighborhoodId: string
  ): Promise<void> {
    this.loading = true;
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/charges-cycles`;

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ chargesCycles: IChargesCycle[] }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ chargesCycles: IChargesCycle[] }>>(urlApi, {
            headers: {
              Authorization: token.toString(),
            },
          }).subscribe({
            next: (response) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) {
                resolve(new ServerResponse(error.error));
              } else {
                reject(error);
              }
            },
          });
        }
      );

      if (response.statusCode === 200) {
        this.chargesCycles = response.data?.chargesCycles.map(
          (cycle) => new ChargesCycle(cycle)
        ) || [];
      } else {
        this.toastService.show({
          header: 'Error',
          body: response.message,
          classname: 'bg-danger text-white',
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.loading = false;
    }
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update(
    chargesCycleId: string,
    body: { cutoffDay?: number; maxPaymentDay?: number },
    neighborhoodId: string
  ): Promise<boolean> {
    let success = false;
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/charges-cycles/${chargesCycleId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando ciclo de cobro...';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>((resolve, reject) => {
        this.http.put<IServerResponse>(
          urlApi,
          JSON.stringify(body),
          {
            headers: {
              Authorization: token.toString(),
              'Content-Type': 'application/json',
            },
          }
        ).subscribe({
          next: (response) => resolve(new ServerResponse(response)),
          error: (error) => {
            if (error.error) {
              resolve(new ServerResponse(error.error));
            } else {
              reject(error);
            }
          },
        });
      });

      if (response.statusCode === 200) {
        success = true;
        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white',
        });
        await this.getAll(neighborhoodId);
      } else {
        this.toastService.show({
          header: 'Error',
          body: response.message,
          classname: 'bg-danger text-white',
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.spinnerService.loading = false;
      this.spinnerService.message = '';
    }

    return success;
  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  async delete(
    chargesCycleId: string,
    neighborhoodId: string
  ): Promise<boolean> {
    let success = false;
    const urlApi = `${environment.urlApi}/neighborhoods/${neighborhoodId}/charges-cycles/${chargesCycleId}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando ciclo de cobro...';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>((resolve, reject) => {
        this.http.delete<IServerResponse>(urlApi, {
          headers: { Authorization: token.toString() },
        }).subscribe({
          next: (response) => resolve(new ServerResponse(response)),
          error: (error) => {
            if (error.error) {
              resolve(new ServerResponse(error.error));
            } else {
              reject(error);
            }
          },
        });
      });

      if (response.statusCode === 200) {
        success = true;
        this.toastService.show({
          body: response.message,
          classname: 'bg-success text-white',
        });
        await this.getAll(neighborhoodId);
      } else {
        this.toastService.show({
          header: 'Error',
          body: response.message,
          classname: 'bg-danger text-white',
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.spinnerService.loading = false;
      this.spinnerService.message = '';
    }

    return success;
  }

}
